/**
 * In-app user manual — Markdown renderer.
 *
 * Loads every page (.md) and screenshot (images/*) under src/help/ at build time,
 * and turns "<domain>/<slug>" into rendered HTML on demand.
 *
 * Markdown files are loaded as raw strings via the webpack rule in vue.config.js
 * (`help-markdown` -> type: 'asset/source'). Images are loaded as hashed build URLs.
 */

import MarkdownIt from "markdown-it";
import anchor from "markdown-it-anchor";

/* ------------------------------------------------------------------ */
/* Build-time content maps                                              */
/* ------------------------------------------------------------------ */

const mdContext = require.context("./", true, /\.md$/);
const imgContext = require.context(
  "./",
  true,
  /\/images\/.*\.(png|jpe?g|gif|svg|webp)$/
);

function unwrap(mod) {
  return mod && typeof mod === "object" && "default" in mod ? mod.default : mod;
}

// key: "getting-started/overview"  ->  raw markdown string
const pages = {};
mdContext.keys().forEach((key) => {
  const clean = key.replace(/^\.\//, "").replace(/\.md$/, "");
  pages[clean] = unwrap(mdContext(key));
});

// key: "getting-started/images/overview-01.png"  ->  hashed URL
const images = {};
imgContext.keys().forEach((key) => {
  const clean = key.replace(/^\.\//, "");
  images[clean] = unwrap(imgContext(key));
});

/* ------------------------------------------------------------------ */
/* Markdown engine                                                      */
/* ------------------------------------------------------------------ */

const md = new MarkdownIt({
  html: false,
  linkify: true,
  typographer: true,
  breaks: false,
}).use(anchor, {
  // Keep heading text as plain text inside the <h*>. Do NOT wrap it in an
  // <a> permalink — a hidden/!display:none anchor style then swallows the
  // heading. id only, nothing else.
  tabIndex: false,
  permalink: false,
});

/* ------------------------------------------------------------------ */
/* Public API                                                           */
/* ------------------------------------------------------------------ */

export function hasPage(domain, slug) {
  return Object.prototype.hasOwnProperty.call(pages, `${domain}/${slug}`);
}

export function rawPage(domain, slug) {
  return pages[`${domain}/${slug}`] || null;
}

/**
 * Render "<domain>/<slug>" to HTML.
 *  - relative <img src="images/x.png"> -> hashed build URL, or a visible
 *    "Screenshot pending" placeholder when the file doesn't exist yet
 *  - in-content links ending in .md get a data-help-link marker so the
 *    page shell can intercept the click and route in-app
 */
export function renderPage(domain, slug) {
  const raw = rawPage(domain, slug);
  if (raw == null) return null;

  let html = md.render(raw);
  html = rewriteImages(html, domain);
  html = markMarkdownLinks(html);
  return html;
}

/** First H1 text of a page, for breadcrumbs / <title>. */
export function pageHeading(domain, slug) {
  const raw = rawPage(domain, slug);
  if (!raw) return null;
  const m = raw.match(/^#\s+(.+)$/m);
  return m ? m[1].trim() : null;
}

export function imageUrl(domain, name) {
  return images[`${domain}/images/${name}`] || null;
}

/* ------------------------------------------------------------------ */
/* Internals                                                            */
/* ------------------------------------------------------------------ */

function rewriteImages(html, domain) {
  return html.replace(
    /<img\b[^>]*>/g,
    (tag) => {
      const srcMatch = tag.match(/\ssrc="([^"]*)"/);
      const altMatch = tag.match(/\salt="([^"]*)"/);
      const alt = altMatch ? altMatch[1] : "";
      const src = srcMatch ? srcMatch[1] : "";

      // Only touch relative "images/..." references; leave anything else alone.
      const rel = src.replace(/^\.\//, "");
      if (!/^images\//.test(rel)) return tag;

      const name = rel.replace(/^images\//, "");
      const url = imageUrl(domain, name);
      if (url) {
        return (
          `<img src="${url}" alt="${escapeAttr(alt)}" loading="lazy" ` +
          `class="help-shot" data-full="${url}">`
        );
      }
      return (
        `<span class="help-shot-pending" role="img" aria-label="Screenshot pending: ${escapeAttr(
          alt
        )}">Screenshot pending: ${escapeHtml(alt || name)}</span>`
      );
    }
  );
}

function markMarkdownLinks(html) {
  return html.replace(/<a\b([^>]*)href="([^"]+)"([^>]*)>/g, (m, pre, href, post) => {
    if (/\.md(#[^"]*)?$/.test(href) && !/^https?:\/\//.test(href)) {
      return `<a${pre}href="${href}"${post} data-help-link="1">`;
    }
    return m;
  });
}

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}
function escapeAttr(s) {
  return escapeHtml(s).replace(/"/g, "&quot;");
}

/* Exposed for the link-checker script. */
export const _debug = { pages, images };
